import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMicrophone, faStop, faPause, faPlay, faTrash, faCheck, faCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAudioRecorder, voiceNoteFile } from '../hooks/useAudioRecorder';
import Waveform from './Waveform';
import '../styles/audio.css';

const fmt = (ms) => {
    const total = Math.floor(ms / 1000);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Record a voice note for the discussion.
 *
 * Always previews before attaching: a note you cannot hear back before sending
 * is a note you will re-record anyway. `onAttach` receives the file plus the
 * waveform and duration measured during recording, so the server can store them
 * alongside the audio.
 */
const AudioRecorder = ({ onAttach, disabled, autoStart = false, onClose }) => {
    const {
        isSupported, status, error, recordingTimeMs, liveLevels,
        recordedBlob, waveform, maxMs,
        startRecording, stopRecording, discardRecording
    } = useAudioRecorder();

    const previewUrl = React.useMemo(
        () => (recordedBlob ? URL.createObjectURL(recordedBlob) : null),
        [recordedBlob]
    );

    // An object URL that outlives its blob leaks; revoke it when it changes.
    React.useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const startedRef = React.useRef(false);

    // Mounting already-recording is the point when the composer swaps the text
    // box for the waves: the mic click was the start instruction.
    React.useEffect(() => {
        if (autoStart && !startedRef.current && status === 'idle') {
            startedRef.current = true;
            startRecording();
        }
    }, [autoStart, status, startRecording]);

    const [playing, setPlaying] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const audioRef = React.useRef(null);

    if (!isSupported) return null;

    const attach = () => {
        if (!recordedBlob) return;
        const file = voiceNoteFile(recordedBlob);
        onAttach(file, { waveform, durationMs: recordingTimeMs });
        discardRecording();
        onClose?.();
    };

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) { el.play(); setPlaying(true); }
        else { el.pause(); setPlaying(false); }
    };

    return (
        <div className={`ar ${status !== 'idle' ? 'is-active' : ''}`}>
            {status === 'idle' && !autoStart && (
                <button type="button" className="ar-trigger" onClick={startRecording} disabled={disabled}
                    title="Record a voice note">
                    <FontAwesomeIcon icon={faMicrophone} />
                    <span>Voice note</span>
                </button>
            )}

            {status === 'requesting' && (
                <span className="ar-hint">Waiting for microphone permission…</span>
            )}

            {(status === 'recording' || status === 'paused') && (
                <div className="ar-live">
                    <span className={`ar-dot ${status === 'recording' ? 'is-live' : ''}`}>
                        <FontAwesomeIcon icon={faCircle} />
                    </span>

                    {/* The bars are the live signal, so a dead mic is obvious
                        before a two-minute take rather than after it. */}
                    <div className="ar-bars" aria-hidden="true">
                        {(liveLevels.length ? liveLevels : [0]).map((l, i) => (
                            <span key={i} className="ar-bar" style={{ height: `${Math.max(8, l * 100)}%` }} />
                        ))}
                    </div>

                    <span className="ar-time">
                        {fmt(recordingTimeMs)}
                        <em>/ {fmt(maxMs)}</em>
                    </span>

                    {/*
                      * One control while recording, as on WhatsApp: stop.
                      *
                      * Pause and discard used to sit beside it, and three small
                      * icons next to a running mic meant people hit the wrong
                      * one. Deciding whether to keep the note belongs to the
                      * preview below, where it can be heard first and then
                      * attached or thrown away.
                      */}
                    <button type="button" className="ar-icon is-stop" onClick={stopRecording}
                        title="Stop recording" aria-label="Stop recording">
                        <FontAwesomeIcon icon={faStop} />
                    </button>
                </div>
            )}

            {status === 'preview' && (
                <div className="ar-preview">
                    <button type="button" className="ar-play" onClick={togglePlay}
                        title={playing ? 'Pause' : 'Play back'}>
                        <FontAwesomeIcon icon={playing ? faPause : faPlay} />
                    </button>

                    <Waveform
                        peaks={waveform}
                        progress={progress}
                        onSeek={(p) => {
                            const el = audioRef.current;
                            if (el && el.duration) { el.currentTime = p * el.duration; setProgress(p); }
                        }}
                    />

                    <span className="ar-time">{fmt(recordingTimeMs)}</span>

                    <button type="button" className="ar-icon is-discard"
                        onClick={() => { discardRecording(); onClose?.(); }} title="Discard">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                    <button type="button" className="ar-attach" onClick={attach} title="Attach to message">
                        <FontAwesomeIcon icon={faCheck} /> Attach
                    </button>

                    <audio
                        ref={audioRef}
                        src={previewUrl}
                        onTimeUpdate={(e) => {
                            const el = e.currentTarget;
                            if (el.duration && isFinite(el.duration)) setProgress(el.currentTime / el.duration);
                        }}
                        onEnded={() => { setPlaying(false); setProgress(0); }}
                        hidden
                    />
                </div>
            )}

            {error && <span className="ar-error">{error}</span>}
        </div>
    );
};

export default AudioRecorder;
